import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, setApiKey, errorMessage } from "@/lib/api";
import { MagicAdLogo } from "@/components/Brand";
import { useSeo } from "@/lib/seo";
import { ArrowRight, KeyRound, ShieldCheck } from "lucide-react";

/** Separate admin entrance: the internal Lab is unlocked with the service key, never with a customer login. */
export const ApiKeyGate = ({ onUnlocked }) => {
  useSeo({ title: "Lab · Logg inn", noindex: true });
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      setApiKey(key.trim());
      const r = await api.post("/lab/auth/verify");
      onUnlocked(r.data);
    } catch (err) {
      setApiKey("");
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--charcoal))] text-[hsl(var(--offwhite))] flex items-center justify-center px-5 py-10 relative overflow-hidden">
      <div className="absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-[hsl(var(--accent))]/[0.12] blur-3xl" aria-hidden="true" />
      <div className="relative w-full max-w-[440px] space-y-8 rise-in" data-testid="api-key-gate">
        <Link to="/" className="inline-flex"><MagicAdLogo tone="dark" /></Link>
        <div>
          <div className="eyebrow text-[hsl(var(--offwhite))]/50">Intern innlogging</div>
          <h1 className="display-sm mt-3 text-[40px] sm:text-[48px]">DigiHome Lab<span className="dot">.</span></h1>
          <p className="mt-3 text-[16px] text-[hsl(var(--offwhite))]/70">Internt verktøy for MagicAd-bildemotoren. Skriv inn tjenestenøkkelen for å fortsette.</p>
        </div>
        <form onSubmit={submit} className="rounded-[20px] bg-white/[0.06] ring-1 ring-white/10 backdrop-blur-md p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="api-key" className="text-[13.5px] text-[hsl(var(--offwhite))]/80">Tjenestenøkkel (X-API-Key)</Label>
            <div className="relative">
              <KeyRound className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[hsl(var(--offwhite))]/50" />
              <Input id="api-key" data-testid="api-key-input" className="pl-10 font-mono bg-white/[0.06] border-white/15 text-[hsl(var(--offwhite))] placeholder:text-[hsl(var(--offwhite))]/35 focus-visible:border-white/40 focus-visible:ring-white/10" placeholder="dh_live_…" value={key} onChange={(e) => setKey(e.target.value)} autoFocus />
            </div>
            <p className="text-[12.5px] text-[hsl(var(--offwhite))]/55 inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Samme nøkkel som DigiHome-backend bruker. Lagres kun i denne nettleseren.</p>
          </div>
          {error && <div className="rounded-[10px] surface-danger border px-3.5 py-2.5 text-[14px]" data-testid="api-key-error">{error}</div>}
          <Button type="submit" size="lg" className="w-full" disabled={busy || !key.trim()} data-testid="api-key-submit">{busy ? "Sjekker…" : <>Fortsett<ArrowRight className="h-4 w-4" /></>}</Button>
        </form>
        <p className="text-[14px] text-[hsl(var(--offwhite))]/55">Kunde? <Link to="/logg-inn" className="text-[hsl(var(--offwhite))] underline-offset-4 hover:underline" data-testid="gate-to-customer-login">Logg inn i MagicAd</Link></p>
      </div>
    </div>
  );
};
